<template>
  <Popover ref="popover" :showHeader="true" :headerTitle="title" sizeClass="popover-xlarge" :appendToBody="appendToBody" :placement="placement">
    <div class="p-2">
      <LoadingPanel :loading="loading" :loadingError="loadingError" />
      <div v-if="!loading">
        <div class="float-right">
          <LabelBadge :labelClass="`label-taskitem-status-${taskItem.status}`">{{taskItem.status | formatLookupName(TaskItemStatusTypeLookup)}}</LabelBadge>
        </div>
        <div class="font-weight-bold">Name</div>
        <div>{{taskItem.name}}</div>
        <div v-if="isTaskItemLocationEnabled">
          <div class="font-weight-bold">Address</div>
          <div><LinkGoogleMap :fullAddress="taskItem.locationAddress" /></div>
        </div>
        <div v-if="isTaskItemStoppageType">
          <div class="font-weight-bold">Stoppage Labels</div>
          <div><LabelTags :labelList="taskItem.stoppageLabels" /></div>
        </div>
        <FormHorizontalRule />
        <div class="row">
          <div class="col-6">
            <div v-if="isTaskItemActive || isTaskItemCompleted">
              <div class="font-weight-bold">Started Time</div>
              <div>{{formatDateTime(taskItem.startedDate)}}</div>
            </div>

            <div v-if="isTaskItemLocationEnabled">
              <div class="font-weight-bold">Estimated Arrival</div>
              <div>{{formatDateTime(taskItem.estimatedArrivalDate)}}</div>
            </div>

            <div v-if="taskItem.isResourceArrived && isTaskItemLocationEnabled">
              <div class="font-weight-bold">Arrival Time</div>
              <div>{{formatDateTime(taskItem.arrivalDate)}}</div>
            </div>
            <div v-if="taskItem.estimatedCompletionDate">
              <div class="font-weight-bold">Estimated Completion Time</div>
              <div>{{formatDateTime(taskItem.estimatedCompletionDate)}}</div>
            </div>

            <div v-if="isTaskItemCompleted">
              <div class="font-weight-bold">Completed Time</div>
              <div>{{formatDateTime(taskItem.completedDate)}}</div>
            </div>
            
            <div v-if="taskItem.estimatedLeaveDate && isTaskItemLocationEnabled">
              <div class="font-weight-bold">Estimated Leaving Time</div>
              <div>{{formatDateTime(taskItem.estimatedLeaveDate)}}</div>
            </div>
            <div v-if="isTaskItemCompleted && taskItem.leaveDate && isTaskItemLocationEnabled">
              <div class="font-weight-bold">Left At</div>
              <div>{{formatDateTime(taskItem.leaveDate)}}</div>
            </div>
          </div>

          <div class="col-6">
            <div v-if="isTaskItemDespatchType">
              <div class="font-weight-bold">Scanned In</div>
              <div>{{totalScannedInQuantity}} Total Quantity</div>
              <div class="font-weight-bold">Scanned Out</div>
              <div>{{totalScannedOutQuantity}} Total Quantity</div>
            </div>

            <div v-if="isTaskItemCompleted">
              <div class="font-weight-bold">Signoff</div>
              <div>{{contactSignoffDescription}}</div>
            </div>

            <div v-if="isTaskItemCompleted && isTaskItemLateArrival">
              <div class="font-weight-bold">Late Arrival Reason</div>
              <div>{{taskItem.lateArrivalReason}}</div>
              <div class="font-weight-bold">Late Arrival Labels</div>
              <div><LabelTags :labelList="taskItem.lateArrivalLabels" class="mb-2" /></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Popover>
</template>

<script>
import Popover from '@/components/popover'

import { TaskItemStatusTypes, TaskItemStatusTypeLookup, TaskSignoffTypeLookup, TaskSignoffTypes, TaskItemTypes } from '@/store/types'

export default {
  name: 'TaskItemPopover',
  components: {
    Popover
  },
  props: {
    placement: {
      type: String,
      default: 'bottom'
    },
    appendToBody: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      loadingError: '',
      task: {},
      taskItem: {},
      scannedDetails: []
    }
  },
  created() {
    this.TaskItemStatusTypeLookup = TaskItemStatusTypeLookup
    this.TaskSignoffTypeLookup = TaskSignoffTypeLookup
  },
  computed: {
    isTaskLocationTracking() {
      return this.task.taskType.isLocationTrackingEnabled
    },
    title() {
      if(this.loading)
        return 'Loading'

      if(this.task && this.taskItem) {
        if(this.taskItem.length > 1)
          return `${this.task.name} - Item #${this.taskItem.sortOrder+1}`

        return `${this.task.name} - ${this.taskItem.name}`
      }
      return 'Task Item'
    },
    isTaskItemActive() {
      return this.taskItem.status == TaskItemStatusTypes.active
    },
    isTaskItemCompleted() {
      return this.taskItem.status == TaskItemStatusTypes.completed
    },
    totalScannedInQuantity() {
      return this.scannedDetails.reduce((curValue, scannedInfo) => curValue + scannedInfo.scannedInQuantity, 0)
    },
    totalScannedOutQuantity() {
      return this.scannedDetails.reduce((curValue, scannedInfo) => curValue + scannedInfo.scannedOutQuantity, 0)
    },
    contactSignoffDescription() {
      switch(this.taskItem.contactSignoffType) {
        case TaskSignoffTypes.none:
          return 'None'
        case TaskSignoffTypes.signature:
          return `Signature By '${this.taskItem.contactSignoffName}'`
        case TaskSignoffTypes.photoProof:
          return `Photo Proof. Details: '${this.taskItem.contactSignoffPhotoDetails}'`
      }
      return 'None'
    },
    isTaskItemLateArrival() {
      return this.taskItem.isLateArrival
    },
    isTaskItemDespatchType() {
      return this.taskItem.type == TaskItemTypes.orderDespatch
    },
    isTaskItemStoppageType() {
      return this.taskItem.type == TaskItemTypes.stoppage
    },
    isTaskItemLocationEnabled() {
      return this.taskItem.isLocationEnabled
    }
  },
  methods: {
    displayFromElement(element, task, taskItem) {
      this.$refs.popover.displayFromElement(element)

      // Load task / scanned items
      this.requestData(task.id, taskItem.id)

      // Reset
      this.task = {}
      this.taskItem = {}
      this.scannedDetails = []
    },
    requestData(taskId, taskItemId) {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.tasksService.getTask(taskId),
        this.$apiService.tasksService.getTaskScannedItems(taskId)
      ])
      .then(results => {
        this.task = results[0].data
        this.taskItem = this.task.taskItems.find(taskItem => taskItem.id == taskItemId)

        // Get scanned info
        var scannedTaskItem = results[1].data.find(taskItem => taskItem.id == taskItemId)
        if(scannedTaskItem)
          this.scannedDetails = scannedTaskItem.taskItemScannedItems

        this.$nextTick(() => {
          // Update popover now that the popover height is set
          this.$refs.popover.updatePopper()
        })
      })
      .catch(err => {
        this.loadingError = 'Error loading data'
        console.error(err)
      })
      .finally(() => {
        this.loading = false
      })
    }
  }
}
</script>